import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { api, errMsg, fmt } from "../../lib/api";
import { useCategories } from "../../lib/categories";
import { Field, btnPrimary, inputCls } from "../../components/ui";

export function RefundDialog({ target, onClose, onDone }) {
  const [f, setF] = useState({ reference: "", reason: "", transfer_confirmed: false });
  if (!target) return null;
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/finance/refunds", { kind: target.kind, id: target.id, ...f }); toast.success("Reembolso registado no histórico financeiro"); onDone(); }
    catch (e2) { toast.error(errMsg(e2)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="refund-dialog">
        <DialogHeader><DialogTitle>Reembolso · {target.number} · {fmt(target.amount)}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-3">
          <Field label="Referência da transferência de reembolso"><input required minLength={3} value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} className={inputCls} data-testid="refund-reference-input" /></Field>
          <Field label="Motivo"><input required minLength={3} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className={inputCls} data-testid="refund-reason-input" /></Field>
          <label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={f.transfer_confirmed} onChange={(e) => setF({ ...f, transfer_confirmed: e.target.checked })} className="mt-1" data-testid="refund-confirm-checkbox" />
            <span>Confirmo que o valor foi efetivamente devolvido ao cliente. O registo não pode ser apagado; correções são feitas com novos movimentos.</span></label>
          <button disabled={!f.transfer_confirmed} className={btnPrimary} data-testid="refund-submit-btn">Registar reembolso</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function PayoutDialog({ row, onClose, onDone }) {
  const [f, setF] = useState({ amount: "", reference: "", reason: "" });
  if (!row) return null;
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/finance/payouts", { store_id: row.store_id, ...f, amount: Number(f.amount) }); toast.success("Pagamento à loja registado"); onDone(); }
    catch (e2) { toast.error(errMsg(e2)); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="payout-dialog">
        <DialogHeader><DialogTitle>Pagamento à loja · {row.store_name || row.store_id.slice(0, 8)}</DialogTitle></DialogHeader>
        <p className="text-sm text-gray-600">Pendente: <strong>{fmt(row.pending)}</strong></p>
        <form onSubmit={submit} className="flex flex-col gap-3">
          <Field label="Valor (STN)"><input required type="number" min="0.01" step="0.01" max={row.pending} value={f.amount} onChange={set("amount")} className={inputCls} data-testid="payout-amount-input" /></Field>
          <Field label="Referência da transferência"><input required minLength={3} value={f.reference} onChange={set("reference")} className={inputCls} data-testid="payout-reference-input" /></Field>
          <Field label="Motivo / período"><input required minLength={3} value={f.reason} onChange={set("reason")} className={inputCls} data-testid="payout-reason-input" /></Field>
          <button className={btnPrimary} data-testid="payout-submit-btn">Registar pagamento</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function AdjustmentForm({ onDone }) {
  const [f, setF] = useState({ amount: "", reason: "", reference: "", reverses: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/finance/adjustments", { ...f, amount: Number(f.amount), reverses: f.reverses || null }); toast.success("Correção registada como novo movimento"); setF({ amount: "", reason: "", reference: "", reverses: "" }); onDone(); }
    catch (e2) { toast.error(errMsg(e2)); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="bo-card grid gap-3 p-4 sm:grid-cols-5" data-testid="adjustment-form">
      <Field label="Valor (+/- STN)"><input required type="number" step="0.01" value={f.amount} onChange={set("amount")} className={inputCls} data-testid="adjustment-amount-input" /></Field>
      <Field label="Motivo"><input required minLength={5} value={f.reason} onChange={set("reason")} className={inputCls} data-testid="adjustment-reason-input" /></Field>
      <Field label="Referência"><input value={f.reference} onChange={set("reference")} className={inputCls} data-testid="adjustment-reference-input" /></Field>
      <Field label="Corrige o movimento (ID, opcional)"><input value={f.reverses} onChange={set("reverses")} className={inputCls} data-testid="adjustment-reverses-input" /></Field>
      <div className="flex items-end"><button className={btnPrimary} data-testid="adjustment-submit-btn">Registar correção</button></div>
    </form>
  );
}

export function CommissionsEditor() {
  const CATEGORIES = useCategories();
  const [c, setC] = useState(null);
  const [reason, setReason] = useState("");
  useEffect(() => { api.get("/admin/finance/commissions").then(({ data }) => setC(data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!c) return null;
  const num = (v) => (v === "" ? undefined : Number(v));
  const setMap = (k, key, v) => { const m = { ...c[k] }; if (v === "") delete m[key]; else m[key] = num(v); setC({ ...c, [k]: m }); };
  const save = async (e) => {
    e.preventDefault();
    try { const { data } = await api.put("/admin/finance/commissions", { default: c.default, categories: c.categories, stores: c.stores, reason }); setC({ ...c, ...data }); setReason(""); toast.success("Comissões guardadas"); } catch (e2) { toast.error(errMsg(e2)); }
  };
  const pct = "w-24 rounded-lg border border-gray-200 px-2 py-1 text-right text-sm";
  return (
    <form onSubmit={save} className="bo-card flex flex-col gap-4 p-4" data-testid="commissions-editor">
      <p className="text-sm text-gray-600">Percentagem cobrada sobre o subtotal dos produtos. Prioridade: loja &gt; categoria &gt; geral. Aplica-se a pagamentos confirmados a partir de agora.</p>
      <label className="flex items-center gap-3 text-sm font-semibold">Comissão geral (%)<input type="number" min="0" max="50" step="0.1" value={c.default} onChange={(e) => setC({ ...c, default: Number(e.target.value) })} className={pct} data-testid="commission-default-input" /></label>
      <div className="grid gap-2 sm:grid-cols-3">{CATEGORIES.map((k) => (
        <label key={k.key} className="flex items-center justify-between gap-2 text-sm">{k.name}<input type="number" min="0" max="50" step="0.1" placeholder="geral" value={c.categories[k.key] ?? ""} onChange={(e) => setMap("categories", k.key, e.target.value)} className={pct} data-testid={`commission-cat-${k.key}`} /></label>))}</div>
      <div className="grid gap-2 sm:grid-cols-2">{c.store_options.map((s) => (
        <label key={s.id} className="flex items-center justify-between gap-2 text-sm">{s.name}<input type="number" min="0" max="50" step="0.1" placeholder="—" value={c.stores[s.id] ?? ""} onChange={(e) => setMap("stores", s.id, e.target.value)} className={pct} data-testid={`commission-store-${s.id}`} /></label>))}</div>
      <div className="flex flex-wrap items-end gap-3"><Field label="Motivo da alteração"><input required minLength={3} value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls} data-testid="commission-reason-input" /></Field>
        <button className={btnPrimary} data-testid="commission-save-btn">Guardar comissões</button></div>
    </form>
  );
}
